const RELOAD_FLAG_KEY = "mtn-chunk-reload-attempted";

const STALE_CHUNK_MESSAGE =
  /[fF][aA][iI][lL][eE][dD] [tT][oO] [fF][eE][tT][cC][hH] [dD][yY][nN][aA][mM][iI][cC][aA][lL][lL][yY] [iI][mM][pP][oO][rR][tT][eE][dD] [mM][oO][dD][uU][lL][eE]|[iI][mM][pP][oO][rR][tT][iI][nN][gG] [aA] [mM][oO][dD][uU][lL][eE] [sS][cC][rR][iI][pP][tT] [fF][aA][iI][lL][eE][dD]|^[cC][aA][nN][nN][oO][tT] [fF][iI][nN][dD] [mM][oO][dD][uU][lL][eE] '[^']+'$/u;

let reloaded = (() => {
  try {
    return sessionStorage.getItem(RELOAD_FLAG_KEY) === "1";
  } catch {
    return false;
  }
})();

// In-memory; once true, `before_send` drops parallel chunk failures from the
// same page load so PostHog only sees the first one. Distinct from `reloaded`,
// which persists across page loads — a post-reload page where the chunk still
// fails gets one real kill-metric event, then `postReloadFailureReported`
// drops the same-load duplicates too.
let reloadInFlight = false;
let postReloadFailureReported = false;

export const armChunkReload = () => {
  if (reloaded) {
    return false;
  }

  // Persist the loop guard before doing anything reload-adjacent. If storage
  // throws (private mode, disabled), skip the reload — a broken page is
  // better than a reload loop we can't guard across page loads.
  try {
    sessionStorage.setItem(RELOAD_FLAG_KEY, "1");
  } catch {
    reloaded = true;
    reloadInFlight = true;

    return false;
  }

  reloaded = true;
  reloadInFlight = true;

  return true;
};

/**
 * Called from util/posthog.js `before_send`. Returns true if PostHog should
 * drop this $exception event as a chunk-reload duplicate.
 *
 * In practice the addEventListener handler in chunk-reload.js runs before posthog-js's
 * `window.onunhandledrejection` property handler fires `before_send`:
 * main.js calls `installChunkReloadHandler()` before `initPostHog()`, and
 * posthog-js further delays the property assignment by lazy-loading the
 * exception-autocapture bundle from CDN. By the spec, IDL-attribute handlers
 * fire at the position of their most recent assignment relative to existing
 * addEventListener listeners — so the order is not guaranteed in the abstract.
 * The branches below are defensive in both orders: if the property handler
 * somehow fires first on a fresh page (reloadInFlight still false), the
 * final branch drops conservatively because we're about to reload anyway.
 *
 * @param {unknown} exceptionValue - the $exception_list[0].value string
 * @returns {boolean} true if before_send should return null for this event
 */
export const shouldDropChunkException = (exceptionValue) => {
  if (!STALE_CHUNK_MESSAGE.test(String(exceptionValue))) {
    return false;
  }

  // Same-page-load duplicates after reload() has armed. The kill metric on
  // this page is the `chunk_reload_triggered` event, not the underlying
  // exceptions.
  if (reloadInFlight) {
    return true;
  }

  // Post-reload page where the chunk STILL fails. Let the first event through
  // as the genuine "reload didn't fix it" kill metric; drop the rest.
  if (reloaded) {
    if (postReloadFailureReported) {
      return true;
    }

    postReloadFailureReported = true;

    return false;
  }

  // Fresh page where the `unhandledrejection` handler has not yet run for this
  // chunk. The handler fires reload() immediately after; later events
  // hit the `reloadInFlight` branch above. Drop conservatively — we're about
  // to reload, the exception itself has no diagnostic value here.
  return true;
};

export const hasReloaded = () => reloaded;

export const isStaleChunkException = (value) =>
  STALE_CHUNK_MESSAGE.test(String(value));
